import React, { useState, useEffect } from 'react';
import { graphql } from 'babel-plugin-relay/macro';
import {
	RelayEnvironmentProvider,
	loadQuery,
	usePreloadedQuery,
} from 'react-relay/hooks';
import {
	BrowserRouter as Router,
	Switch,
	Route,
	Redirect,
} from 'react-router-dom';

import environment from './relay-env';
// import 'bootstrap/dist/css/bootstrap.min.css';

import HeaderThemeContext from './context/HeaderThemeContext';

import './uikit/UikitStyles/UikitLayout.sass';
import './uikit/TypoContainer/TypoContainer';
import './Base.sass';
import './Grid.sass';

import { ROUTER_TYPES, DICTIONARIES } from './utils/constants';

import Header from './components/Header';
// import Routes from './components/Routes';
import Home from './pages/Home';
import About from './pages/About';
import Faq from './pages/Faq';
import NotFound from './pages/NotFound';
import ServerError from './pages/ServerError';
import Policy from './pages/Policy';
import CodeOfConduct from './pages/CodeOfConduct';
import BecomePartner from './pages/BecomePartner';
import BecomeSpeaker from './pages/BecomeSpeaker';
import ReportAccident from './pages/ReportAccident';
import SpeakerContract from './pages/SpeakerContract';
import RegisterForEvent from './pages/RegisterForEvent';
import PartnersPage from './pages/PartnersPage';
import SpeakersPage from './pages/SpeakersPage';
import SpeakerPage from './pages/SpeakerPage';
import Agenda from './pages/Agenda';
import Footer from './components/Footer';
import { AppQuery } from './__generated__/AppQuery.graphql';
import { Speakers, Themes } from './types';

const { Suspense } = React;
const queryApp = graphql`
	query AppQuery {
		umbraco {
			content {
				byType {
					...SpeakersPageQuery
					...PartnersPageQuery
					...Home_query_speakers
					...Home_query_partners
					...SpeakerPageQuery
					...AgendaPageQuery
				}

				getAllDictionary {
					key
					value
				}

				atRoot {
					errorPage {
						...NotFound
					}
					home {
						items {
							...Home_query
							# ...Header_query
							...FaqQuery
							...AboutQuery
							...PolicyQuery
							...CodeOfConductQuery
							...BecomePartnerQuery
							...RegisterForEventQuery
							...BecomeSpeakerQuery
							...SpeakerContractQuery
							...ReportAccidentQuery

							themes {
								... on ThemesPublishedContent {
									title
									technologies
								}
							}
							speaker {
								... on SpeakerPublishedContent {
									_id
									_name
									_url
									speakerName
									speakerPreviewDescription
									speakerImage
									facebookLink
									twitterLink
									linkedinLink
									location {
										... on LocationPublishedContent {
											city
											countryName
											country {
												... on FilePublishedMedia {
													_url
												}
											}
										}
									}
								}
							}
						}
					}
				}
			}
		}
	}
`;

const preloadedQuery = loadQuery(environment, queryApp, {
	/* query variables */
});

interface Props {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	preloadedQuery: any;
}

function App(props: Props) {
	const data = usePreloadedQuery<AppQuery>(queryApp, props.preloadedQuery);

	const { atRoot } = data.umbraco.content;
	const firstHomeItem = atRoot.home?.items?.[0];

	let themes: Themes;
	let speakers: Speakers;

	if (firstHomeItem) {
		({ themes, speaker: speakers } = firstHomeItem);
	}

	const [formatedDictinary, setFormatedDictionary] = useState<{
		[key: string]: string;
	}>();

	useEffect(() => {
		const newDictionary: { [key: string]: string } = {};
		data.umbraco.content.getAllDictionary?.map(({ key, value }) => {
			if (key && value) {
				newDictionary[key] = value;
			}
		});
		setFormatedDictionary(newDictionary);
	}, [data]);

	return (
		<Router>
			<div className="layout">
				{formatedDictinary && (
					<Header
						// query={firstHomeItem}
						buttonLabel={formatedDictinary[DICTIONARIES.registerButton]}
					/>
				)}
				<main className="layout__content">
					<Switch>
						<Route
							path={`${ROUTER_TYPES.policy}`}
							render={() => firstHomeItem && <Policy query={firstHomeItem} />}
						/>
						<Route
							path={`${ROUTER_TYPES.codeOfConduct}`}
							render={() =>
								firstHomeItem && <CodeOfConduct query={firstHomeItem} />
							}
						/>
						<Route
							path={`${ROUTER_TYPES.becomePartner}`}
							render={() =>
								firstHomeItem && <BecomePartner query={firstHomeItem} />
							}
						/>
						<Route
							path={`${ROUTER_TYPES.speakerContract}`}
							render={() =>
								firstHomeItem && <SpeakerContract query={firstHomeItem} />
							}
						/>
						<Route
							path={`${ROUTER_TYPES.registerForEvent}`}
							render={() =>
								firstHomeItem && <RegisterForEvent query={firstHomeItem} />
							}
						/>
						<Route
							path={`${ROUTER_TYPES.becomeSpeaker}`}
							render={() =>
								firstHomeItem && <BecomeSpeaker query={firstHomeItem} />
							}
						/>
						<Route
							path={`${ROUTER_TYPES.reportAnAccident}`}
							render={() =>
								firstHomeItem && <ReportAccident query={firstHomeItem} />
							}
						/>
						<Route
							path={`${ROUTER_TYPES.faq}`}
							render={() => firstHomeItem && <Faq query={firstHomeItem} />}
						/>
						<Route
							path={`${ROUTER_TYPES.about}`}
							render={() =>
								firstHomeItem && (
									<About
										query={firstHomeItem}
										labels={{
											...formatedDictinary,
										}}
									/>
								)
							}
						/>
						<Route
							path="/partners"
							render={() => (
								<PartnersPage
									query={data.umbraco.content.byType}
									labels={{
										...formatedDictinary,
									}}
								/>
							)}
						/>
						<Route
							exact
							path={ROUTER_TYPES.speakers}
							render={() => (
								<SpeakersPage
									query={data.umbraco.content.byType}
									labels={{
										...formatedDictinary,
									}}
								/>
							)}
						/>
						<Route
							path="/speakers/:currentSpeakerUrl/"
							render={() => <SpeakerPage query={data.umbraco.content.byType} />}
						/>
						<Route
							path="/agenda/:tab?"
							render={() => <Agenda query={data.umbraco.content.byType} />}
						/>
						<Route path="/500" render={() => <ServerError />} />
						<Route
							exact
							path="/"
							render={() =>
								firstHomeItem && (
									<Home
										query={firstHomeItem}
										speakers={data.umbraco.content.byType}
										partners={data.umbraco.content.byType}
										speaker={speakers}
										themes={themes}
										labels={{
											...formatedDictinary,
										}}
									/>
								)
							}
						/>
						<Route
							render={() =>
								atRoot.errorPage && (
									<NotFound query={atRoot.errorPage} type="404" />
								)
							}
						/>
					</Switch>
				</main>
				<Footer content={{ ...formatedDictinary }} />
			</div>
		</Router>
	);
}
function AppRoot() {
	const headerThemeStateHook = useState(false);
	return (
		<RelayEnvironmentProvider environment={environment}>
			<Suspense fallback="Loading...">
				<HeaderThemeContext.Provider value={headerThemeStateHook}>
					<App preloadedQuery={preloadedQuery} />
				</HeaderThemeContext.Provider>
			</Suspense>
		</RelayEnvironmentProvider>
	);
}

export default AppRoot;
